import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  DialogContentText,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  TextSnippet as TemplateIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { MessageTemplate } from '../types';
import apiClient from '../utils/api';

interface AdminMessageInputProps {
  onSendMessage: (content: string, attachments?: File[]) => void;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  recipientName?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  enableSendConfirmation?: boolean;
}

const AdminMessageInput: React.FC<AdminMessageInputProps> = ({
  onSendMessage,
  onTypingStart,
  onTypingStop,
  disabled = false,
  placeholder = 'メッセージを入力... (Shift+Enterで改行)',
  maxLength = 10000,
  recipientName,
  recipientEmail,
  recipientPhone,
  enableSendConfirmation = true,
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // テンプレート関連
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateAction, setTemplateAction] = useState<'create' | 'edit'>('create');
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState<{ title: string; content: string; category: 'general' | 'support' | 'technical' | 'other' }>({ 
    title: '', 
    content: '', 
    category: 'general' 
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<MessageTemplate | null>(null);

  // ドラッグ&ドロップ
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // ファイル制限
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];

  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.csv', '.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx'];

  // タイピング状態管理
  useEffect(() => {
    if (message.trim() && !isTyping) {
      setIsTyping(true);
      onTypingStart?.();
    } else if (!message.trim() && isTyping) {
      setIsTyping(false);
      onTypingStop?.();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (message.trim()) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        onTypingStop?.();
      }, 3000);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [message, isTyping, onTypingStart, onTypingStop]);

  useEffect(() => {
    return () => {
      if (isTyping) {
        onTypingStop?.();
      }
    };
  }, [isTyping, onTypingStop]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= maxLength) {
      setMessage(value);
    }
  };

  // Enterキーの動作変更: Enterは改行のみ（送信は送信ボタンのみ）
  const handleKeyPress = (event: React.KeyboardEvent) => {
    // Enterキーは改行のみ（送信は送信ボタンのみ）
    // 何もしない - デフォルトの改行動作を許可
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || disabled || isSending) {
      return;
    }

    if (enableSendConfirmation) {
      setConfirmDialogOpen(true);
      return;
    }

    await executeSend(trimmedMessage);
  };

  const executeSend = async (messageToSend: string) => {
    setIsSending(true);
    try {
      await onSendMessage(messageToSend, attachedFile ? [attachedFile] : undefined);
      setMessage('');
      setAttachedFile(null);
      setFileError(null);
      setIsTyping(false);
      onTypingStop?.();

      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('メッセージ送信エラー:', error);
      setMessage('');
      setAttachedFile(null);
      setIsTyping(false);
      onTypingStop?.();
    } finally {
      setIsSending(false);
    }
  };

  const handleConfirmSend = async () => {
    setConfirmDialogOpen(false);
    const trimmedMessage = message.trim();
    if (trimmedMessage) {
      await executeSend(trimmedMessage);
    }
  };

  const handleCancelSend = () => {
    setConfirmDialogOpen(false);
  };

  // ファイル添付
  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // ファイルサイズチェック
    if (file.size > MAX_FILE_SIZE) {
      setFileError('ファイルサイズは5MB以下にしてください');
      return;
    }

    // ファイルタイプチェック
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setFileError('画像、PDF、CSV、Excel、Word、PowerPointファイルのみアップロードできます');
      return;
    }

    setAttachedFile(file);
    setFileError(null);
    event.target.value = ''; // 同じファイルを再選択できるようにリセット
  };

  const handleRemoveFile = () => {
    setAttachedFile(null);
    setFileError(null);
  };

  // ファイルバリデーション共通関数
  const validateAndAttachFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setFileError('ファイルサイズは5MB以下にしてください');
      return;
    }
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setFileError('画像、PDF、CSV、Excel、Word、PowerPointファイルのみアップロードできます');
      return;
    }
    setAttachedFile(file);
    setFileError(null);
  };

  // ドラッグ&ドロップハンドラ
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndAttachFile(files[0]);
    }
  };

  // テンプレート関連
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await apiClient.getTemplates();
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (error) {
      console.error('テンプレート取得エラー:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setTemplateMenuAnchor(event.currentTarget);
    await loadTemplates();
  };

  const handleTemplateMenuClose = () => {
    setTemplateMenuAnchor(null);
  };

  const handleTemplateSelect = async (template: MessageTemplate) => {
    try {
      const response = await apiClient.useTemplate(template.id);
      if (response.success) {
        setMessage(template.content);
        textFieldRef.current?.focus();
      }
    } catch (error) {
      console.error('テンプレート使用エラー:', error);
    }
    handleTemplateMenuClose();
  };

  // テンプレート新規作成
  const handleCreateTemplateOpen = () => {
    setTemplateAction('create');
    setTemplateForm({ title: '', content: '', category: 'general' });
    setEditingTemplate(null);
    setTemplateDialogOpen(true);
    handleTemplateMenuClose();
  };

  // テンプレート編集
  const handleEditTemplateOpen = (template: MessageTemplate) => {
    setTemplateAction('edit');
    setTemplateForm({ 
      title: template.title, 
      content: template.content, 
      category: template.category 
    });
    setEditingTemplate(template);
    setTemplateDialogOpen(true);
    handleTemplateMenuClose();
  };

  // テンプレート削除確認
  const handleDeleteTemplateOpen = (template: MessageTemplate) => {
    setDeletingTemplate(template);
    setDeleteDialogOpen(true);
    handleTemplateMenuClose();
  };

  // テンプレート保存
  const handleTemplateSave = async () => {
    try {
      if (templateAction === 'create') {
        await apiClient.createTemplate(templateForm);
      } else if (editingTemplate) {
        await apiClient.updateTemplate(editingTemplate.id, templateForm);
      }
      setTemplateDialogOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error('テンプレート保存エラー:', error);
    }
  };

  // テンプレート削除
  const handleTemplateDelete = async () => {
    if (!deletingTemplate) return;
    
    try {
      await apiClient.deleteTemplate(deletingTemplate.id);
      setDeleteDialogOpen(false);
      setDeletingTemplate(null);
      await loadTemplates();
    } catch (error) {
      console.error('テンプレート削除エラー:', error);
    }
  };

  const remainingChars = maxLength - message.length;
  const isNearLimit = remainingChars <= 500;

  return (
    <Paper
      elevation={2}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        p: 1,
        backgroundColor: '#ffffff',
        borderTop: '1px solid #e0e0e0',
        position: 'relative',
        transition: 'border-color 0.2s ease',
        ...(isDragOver && {
          borderColor: 'primary.main',
          borderStyle: 'dashed',
          borderWidth: 2,
        }),
      }}
    >
      {/* ドラッグ&ドロップ オーバーレイ */}
      {isDragOver && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 'inherit',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              ここにファイルをドロップ
            </Typography>
          </Box>
        </Box>
      )}

      {/* ファイルエラー表示 */}
      {fileError && (
        <Alert severity="error" onClose={() => setFileError(null)} sx={{ mb: 1 }}>
          {fileError}
        </Alert>
      )}

      {/* 添付ファイル表示 */}
      {attachedFile && (
        <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachFileIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2">{attachedFile.name} ({(attachedFile.size / 1024).toFixed(1)} KB)</Typography>
          </Box>
          <IconButton size="small" onClick={handleRemoveFile}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* ファイル添付ボタン */}
        <Tooltip title="ファイル添付 (画像・PDF・Office形式, 5MB以下)">
          <IconButton
            onClick={handleAttachFile}
            disabled={disabled}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <AttachFileIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* テンプレートボタン */}
        <Tooltip title="テンプレート管理">
          <IconButton
            onClick={handleTemplateMenuOpen}
            disabled={disabled}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <TemplateIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* メッセージ入力フィールド */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <TextField
            ref={textFieldRef}
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || isSending}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 20,
                backgroundColor: '#f8f9fa',
                paddingRight: '8px',
                '&:hover': {
                  backgroundColor: '#f1f3f4',
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 12px',
                fontSize: '0.95rem',
              },
            }}
          />

          {isNearLimit && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 8,
                color: remainingChars <= 100 ? 'error.main' : 'warning.main',
                fontSize: '0.65rem',
                backgroundColor: 'rgba(255,255,255,0.9)',
                px: 0.5,
                borderRadius: 0.5,
              }}
            >
              残り {remainingChars.toLocaleString()}
            </Typography>
          )}
        </Box>

        {/* 送信ボタン */}
        <Tooltip title="送信 (送信ボタンクリック)">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!message.trim() || disabled || isSending}
              color="primary"
              size="small"
              sx={{
                width: 36,
                height: 36,
                backgroundColor: message.trim() && !disabled && !isSending ? 'primary.main' : '#e0e0e0',
                color: 'white',
                '&:hover': {
                  backgroundColor: message.trim() && !disabled && !isSending ? 'primary.dark' : '#e0e0e0',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#bdbdbd',
                },
              }}
            >
              {isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* 送信確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSend}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>メッセージ送信確認</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            以下の内容でメッセージを送信しますか？
          </Typography>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              送信先:
            </Typography>
            <Typography variant="body2">
              {recipientName ? `${recipientName}様` : 'お客様'}
            </Typography>
            {recipientEmail && (
              <Typography variant="body2" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                📧 {recipientEmail}
              </Typography>
            )}
            {recipientPhone && (
              <Typography variant="body2" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                📱 {recipientPhone}
              </Typography>
            )}
            {!recipientEmail && !recipientPhone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                ※ 通知先（メール・電話番号）が未設定です
              </Typography>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {recipientEmail && (
                <Typography variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'info.light', color: 'info.dark', borderRadius: 1, fontWeight: 'bold' }}>
                  メール通知
                </Typography>
              )}
              {recipientPhone && (
                <Typography variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'success.light', color: 'success.dark', borderRadius: 1, fontWeight: 'bold' }}>
                  SMS通知
                </Typography>
              )}
            </Box>
          </Box>

          {attachedFile && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                添付ファイル:
              </Typography>
              <Typography variant="body2">
                {attachedFile.name} ({(attachedFile.size / 1024).toFixed(1)} KB)
              </Typography>
            </Box>
          )}

          <Box sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              メッセージ内容:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 120,
                overflow: 'auto'
              }}
            >
              {message}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCancelSend} size="large">
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSend}
            variant="contained"
            size="large"
            disabled={isSending}
          >
            {isSending ? '送信中...' : '送信する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* テンプレートメニュー */}
      <Menu
        anchorEl={templateMenuAnchor}
        open={Boolean(templateMenuAnchor)}
        onClose={handleTemplateMenuClose}
        PaperProps={{
          style: {
            maxHeight: 450,
            width: 380,
          },
        }}
      >
        <MenuItem onClick={handleCreateTemplateOpen}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="subtitle2">新規テンプレート作成</Typography>
        </MenuItem>
        <Divider />
        
        {loadingTemplates ? (
          <MenuItem disabled>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            読み込み中...
          </MenuItem>
        ) : templates.length === 0 ? (
          <MenuItem disabled>
            テンプレートがありません
          </MenuItem>
        ) : (
          templates.map((template) => (
            <MenuItem
              key={template.id}
              sx={{
                flexDirection: 'column',
                alignItems: 'stretch',
                py: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, cursor: 'pointer', flex: 1 }}
                  onClick={() => handleTemplateSelect(template)}
                >
                  {template.title}
                </Typography>
                <Box>
                  <IconButton size="small" onClick={() => handleEditTemplateOpen(template)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteTemplateOpen(template)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.8rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  cursor: 'pointer',
                }}
                onClick={() => handleTemplateSelect(template)}
              >
                {template.content}
              </Typography>
            </MenuItem>
          ))
        )}
      </Menu>

      {/* テンプレート作成・編集ダイアログ */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {templateAction === 'create' ? 'テンプレート新規作成' : 'テンプレート編集'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="タイトル"
            fullWidth
            value={templateForm.title}
            onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            label="内容"
            fullWidth
            multiline
            rows={4}
            value={templateForm.content}
            onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="カテゴリー"
            fullWidth
            select
            value={templateForm.category}
            onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value as 'general' | 'support' | 'technical' | 'other' })}
            SelectProps={{ native: true }}
          >
            <option value="general">一般</option>
            <option value="support">サポート</option>
            <option value="technical">技術</option>
            <option value="other">その他</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleTemplateSave} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>

      {/* テンプレート削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>テンプレート削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{deletingTemplate?.title}」を削除してもよろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleTemplateDelete} color="error" variant="contained">削除</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminMessageInput;

